import { useEffect, useState } from 'react';
import type { CriminalDocumentRow } from '../../types/criminalCaseRequest';
import * as criminalService from '../../services/criminalCaseRequests';
import OverlayModal from './OverlayModal';
import { displayClientFileName } from '../../utils/fileDisplayName';
import { filePreviewKind, type FilePreviewKind } from '../../utils/filePreview';
import { getCriminalPreviewFile } from '../../utils/criminalDocumentPreviewCache';

type DocProps = {
  isOpen: boolean;
  doc: CriminalDocumentRow | null;
  onClose: () => void;
};

type FileProps = {
  isOpen: boolean;
  documentName: string;
  fileName: string;
  originalFileName: string;
  localFile: File | null;
  onClose: () => void;
};

type Props = DocProps | FileProps;

function isFileProps(p: Props): p is FileProps {
  return 'fileName' in p;
}

export default function DocumentPreviewModal(props: Props) {
  const { isOpen, onClose } = props;

  const documentName = isFileProps(props) ? props.documentName : (props.doc?.documentName ?? '');
  const fileName = isFileProps(props) ? props.fileName : (props.doc?.fileName ?? '');
  const originalFileName = isFileProps(props) ? props.originalFileName : (props.doc?.originalFileName ?? '');
  const localFile = isFileProps(props) ? props.localFile : null;
  const docId = isFileProps(props) ? null : props.doc?.id ?? null;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [objectUrl, setObjectUrl] = useState('');
  const [previewKind, setPreviewKind] = useState<FilePreviewKind>('unsupported');

  const displayName = displayClientFileName(originalFileName, fileName);

  useEffect(() => {
    if (!isOpen || !fileName) {
      setLoading(false);
      setError('');
      setObjectUrl('');
      setPreviewKind('unsupported');
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');

      if (localFile) {
        const url = URL.createObjectURL(localFile);
        setObjectUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
        setPreviewKind(filePreviewKind(localFile.name, localFile.type));
        setLoading(false);
        return;
      }

      if (docId) {
        const cached = getCriminalPreviewFile(docId);
        if (cached) {
          if (cancelled) return;
          const url = URL.createObjectURL(cached);
          setObjectUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
          setPreviewKind(filePreviewKind(cached.name, cached.type));
          setLoading(false);
          return;
        }
      }

      const res = await criminalService.downloadCaseFile(fileName);
      if (cancelled) return;
      if (!res.success) {
        setError(res.message ?? 'Could not load document preview.');
        setLoading(false);
        return;
      }

      const url = URL.createObjectURL(res.data);
      setObjectUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
      setPreviewKind(filePreviewKind(displayName, res.data.type));
      setLoading(false);
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [isOpen, fileName, localFile, docId, displayName]);

  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  return (
    <OverlayModal
      isOpen={isOpen}
      overlayClassName="overlay doc-preview-overlay"
      contentClassName="doc-preview-mbox"
      centerVertically
      onClose={onClose}
    >
      <div className="doc-preview-hdr">
        <div className="doc-preview-hdr-text">
          <div className="doc-preview-title">{documentName || 'Document preview'}</div>
          {displayName ? <div className="doc-preview-sub">{displayName}</div> : null}
        </div>
        <button type="button" className="cn-x doc-preview-close" aria-label="Close preview" onClick={onClose}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <div className="doc-preview-body">
        {loading ? (
          <p className="doc-preview-msg">Loading preview…</p>
        ) : error ? (
          <p className="doc-preview-msg doc-preview-msg--err">{error}</p>
        ) : objectUrl && previewKind === 'pdf' ? (
          <iframe title={displayName} className="doc-preview-frame" src={objectUrl} />
        ) : objectUrl && previewKind === 'image' ? (
          <img src={objectUrl} alt={displayName} className="doc-preview-img" />
        ) : objectUrl ? (
          <div className="doc-preview-fallback">
            <p className="doc-preview-msg">Preview is not available for this file type.</p>
            <a className="mfb" href={objectUrl} download={displayName || 'document'}>
              Download file
            </a>
          </div>
        ) : (
          <p className="doc-preview-msg">No preview available.</p>
        )}
      </div>
    </OverlayModal>
  );
}
